<template>
  <div class="modal-overlay" @click.self="closeModal">
    <div class="modal-container glass">
      <!-- 头部 -->
      <div class="modal-header">
        <div class="header-content">
          <span class="crown-icon glass-sm">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
          </span>
          <h2 class="modal-title">{{ $t('expertModal.title') }}</h2>
        </div>
        <button class="close-btn" @click="closeModal">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- 表单内容 -->
      <div class="modal-body">
        <form @submit.prevent="handleSubmit">
          <!-- 第一行：姓名、手机、邮箱 -->
          <div class="form-row three-cols">
            <div class="form-group">
              <label>{{ $t('expertModal.name') }} <span class="required">*</span></label>
              <input
                type="text"
                v-model="form.name"
                :placeholder="$t('expertModal.namePlaceholder')"
                required
                class="glass-sm"
              />
            </div>
            <div class="form-group">
              <label>{{ $t('expertModal.phone') }} <span class="required">*</span></label>
              <input
                type="tel"
                v-model="form.phone"
                :placeholder="$t('expertModal.phonePlaceholder')"
                required
                class="glass-sm"
              />
            </div>
            <div class="form-group">
              <label>{{ $t('expertModal.email') }} <span class="required">*</span></label>
              <input
                type="email"
                v-model="form.email"
                :placeholder="$t('expertModal.emailPlaceholder')"
                required
                class="glass-sm"
              />
            </div>
          </div>

          <!-- 第二行：公司、职位 -->
          <div class="form-row two-cols">
            <div class="form-group">
              <label>{{ $t('expertModal.company') }}</label>
              <input
                type="text"
                v-model="form.company"
                :placeholder="$t('expertModal.companyPlaceholder')"
                class="glass-sm"
              />
            </div>
            <div class="form-group">
              <label>{{ $t('expertModal.position') }}</label>
              <input
                type="text"
                v-model="form.position"
                :placeholder="$t('expertModal.positionPlaceholder')"
                class="glass-sm"
              />
            </div>
          </div>

          <!-- 第三行：行业经验 -->
          <div class="form-group">
            <label>{{ $t('expertModal.experience') }}</label>
            <textarea
              v-model="form.experience"
              rows="5"
              :placeholder="$t('expertModal.experiencePlaceholder')"
              class="glass-sm"
            ></textarea>
          </div>

          <!-- 条款区域 -->
          <div class="terms-section">
            <label class="terms-label-row">
              <input
                type="checkbox"
                v-model="form.acceptTerms"
                class="terms-checkbox"
              />
              <span class="terms-text">
                <span class="required">*</span>
                {{ $t('expertModal.termsText') }}
                <a href="#/expert-agreement" target="_blank" class="terms-link-text">{{ $t('expertModal.termsLink') }}</a>
              </span>
            </label>
            <p v-if="termsError" class="terms-error">{{ $t('expertModal.termsError') }}</p>
          </div>

          <!-- 提交按钮 -->
          <div class="submit-section">
            <button type="submit" class="submit-btn" :disabled="isSubmitting">
              {{ isSubmitting ? $t('expertModal.submitting') : $t('expertModal.submit') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const emit = defineEmits(['close'])

const isSubmitting = ref(false)
const termsError = ref(false)

const form = reactive({
  name: '',
  phone: '',
  email: '',
  company: '',
  position: '',
  experience: '',
  acceptTerms: false
})

const closeModal = () => {
  emit('close')
}

const handleSubmit = async () => {
  // 校验是否同意协议
  if (!form.acceptTerms) {
    termsError.value = true
    return
  }
  termsError.value = false

  isSubmitting.value = true

  // 演示模式：模拟提交延迟
  setTimeout(() => {
    console.log('【演示模式】专家注册信息：', form)
    alert(t('expertModal.demoMessage'))
    isSubmitting.value = false
    closeModal()
  }, 1000)
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 39, 64, 0.75);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-container {
  background: rgba(255, 255, 255, 0.7);
  border-radius: 28px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow:
    0 25px 80px rgba(0, 0, 0, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.2) inset;
  position: relative;
  animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  background: linear-gradient(135deg, rgba(15, 39, 64, 0.95) 0%, rgba(26, 58, 92, 0.92) 100%);
  padding: 24px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 28px 28px 0 0;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 20% 50%, rgba(52, 152, 255, 0.2) 0%, transparent 50%),
    radial-gradient(circle at 80% 50%, rgba(255, 255, 255, 0.05) 0%, transparent 40%);
  pointer-events: none;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  justify-content: center;
  position: relative;
  z-index: 1;
}

.crown-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--secondary-color);
  background: rgba(255, 255, 255, 0.12);
  box-shadow: 0 4px 15px rgba(52, 152, 255, 0.2);
}

.crown-icon svg {
  width: 24px;
  height: 24px;
}

.modal-title {
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.close-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  cursor: pointer;
  padding: 10px;
  border-radius: 10px;
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-50%) scale(1.05);
}

.close-btn:active {
  transform: translateY(-50%) scale(0.98);
}

.close-btn svg {
  width: 20px;
  height: 20px;
}

.modal-body {
  padding: 36px;
}

.form-row {
  display: grid;
  gap: 20px;
  margin-bottom: 20px;
}

.form-row.three-cols {
  grid-template-columns: repeat(3, 1fr);
}

.form-row.two-cols {
  grid-template-columns: repeat(2, 1fr);
}

@media (max-width: 768px) {
  .form-row.three-cols,
  .form-row.two-cols {
    grid-template-columns: 1fr;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  color: var(--primary-color);
  font-size: 0.9rem;
  margin-bottom: 10px;
  font-weight: 600;
}

.required {
  color: #e74c3c;
}

.form-group input,
.form-group textarea {
  padding: 14px 18px;
  border: 1.5px solid rgba(255, 255, 255, 0.7);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.5);
  color: var(--text-dark);
  font-size: 0.98rem;
  transition: all 0.3s ease;
  font-family: inherit;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: rgba(102, 102, 102, 0.6);
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--secondary-color);
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 0 4px rgba(52, 152, 255, 0.15), 0 4px 12px rgba(52, 152, 255, 0.1);
}

.form-group textarea {
  resize: vertical;
  min-height: 120px;
}

.terms-section {
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid rgba(52, 152, 255, 0.15);
}

.terms-label-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  line-height: 1.6;
}

.terms-checkbox {
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  cursor: pointer;
  accent-color: var(--secondary-color);
  margin: 2px 0 0 0;
  padding: 0;
  flex-shrink: 0;
}

.terms-text {
  color: var(--text-light);
  font-size: 0.9rem;
  line-height: 1.6;
}

.terms-link-text {
  color: var(--secondary-color);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  transition: color 0.2s ease;
}

.terms-link-text:hover {
  color: #2980b9;
  text-decoration: underline;
}

.terms-error {
  color: #e74c3c;
  font-size: 0.85rem;
  margin-top: 10px;
  margin-left: 28px;
  font-weight: 500;
}

.submit-section {
  text-align: center;
  margin-top: 32px;
}

.submit-btn {
  background: linear-gradient(135deg, var(--secondary-color) 0%, #2980b9 100%);
  color: #fff;
  border: none;
  padding: 16px 64px;
  border-radius: 14px;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(52, 152, 255, 0.3);
}

.submit-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #2980b9 0%, var(--secondary-color) 100%);
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(52, 152, 255, 0.4);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(-1px);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 16px;
  }

  .modal-container {
    max-height: 95vh;
    border-radius: 24px;
  }

  .modal-header {
    padding: 18px 24px;
    border-radius: 24px 24px 0 0;
  }

  .modal-title {
    font-size: 1.25rem;
  }

  .crown-icon {
    width: 40px;
    height: 40px;
  }

  .crown-icon svg {
    width: 20px;
    height: 20px;
  }

  .close-btn {
    right: 16px;
    padding: 8px;
  }

  .modal-body {
    padding: 24px;
  }

  .form-group label {
    font-size: 0.88rem;
  }

  .form-group input,
  .form-group textarea {
    padding: 12px 16px;
    font-size: 0.95rem;
  }

  .submit-btn {
    padding: 14px 48px;
    font-size: 1rem;
    width: 100%;
  }
}
</style>
